import { isNil } from 'lodash';
import { CSSProperties } from 'react';
import { ViewStyle } from 'react-native';
import { Field } from '@shared/data/entity/fields/Field';
import { isInputButtonComponent, isTimeTrackerComponent } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { CollectionViewAnalytics } from '../../CollectionViewAnalytics';
import { InputButton } from '../staticComponents/InputButton';
import { AddressCollectionViewField } from './AddressCollectionViewField';
import { CombinedTagsCollectionViewField } from './CombinedTagsCollectionViewField';
import { DateCollectionViewField } from './DateCollectionViewField';
import { DateRangeCollectionViewField } from './DateRangeCollectionViewField';
import { EmailCollectionViewField } from './EmailCollectionViewField';
import { EntityBatchCollectionViewField } from './EntityBatchCollectionViewField';
import { FileCollectionViewField } from './FileCollectionViewField';
import { GeolocationCollectionViewField } from './GeolocationCollectionViewField';
import { NumberCollectionViewField } from './NumberCollectionViewField';
import { PersonCollectionViewField } from './PersonCollectionViewField';
import { RelatedCardCollectionViewField } from './RelatedCardCollectionViewField';
import { ScheduledAutomationCollectionViewField } from './ScheduledAutomationCollectionViewField';
import { SignatureCollectionViewField } from './SignatureCollectionViewField';
import { SubformCollectionViewField } from './SubformCollectionViewField';
import { TagCollectionViewField } from './TagCollectionViewField';
import { TextCollectionViewField } from './TextCollectionViewField';
import { TimeTrackerCollectionViewField } from './TimeTrackerCollectionViewField';
import { TodoCollectionViewField } from './TodoCollectionViewField';
import { UnknownCollectionViewField } from './UnknownCollectionViewField';
import { UrlCollectionViewField } from './UrlCollectionViewField';

interface Props {
  field?: Field | null;
  component: API.AnyComponent;
  workflowTemplateId: string;
  entityId: string;
  fieldStyles: FieldStyles;
  showLabel: boolean;
  canUpdateCard: boolean;
  analytics?: CollectionViewAnalytics;
}

export interface FieldStyles {
  addressStyle?: ViewStyle;
  dateStyle?: ViewStyle;
  dateTextStyle?: CSSProperties;
  dateRangeStyle?: ViewStyle;
  dateRangeTextStyle?: CSSProperties;
  emailStyle?: ViewStyle;
  fileStyle?: ViewStyle;
  geolocationStyle?: ViewStyle;
  numberStyle?: ViewStyle;
  personStyle?: ViewStyle;
  personPillStyle?: ViewStyle;
  personPillTextStyle?: CSSProperties;
  relatedCardStyle?: ViewStyle;
  relatedCardPillStyle?: ViewStyle;
  relatedCardPillTextStyle?: CSSProperties;
  scheduledAutomationStyle?: ViewStyle;
  scheduledAutomationTextStyle?: CSSProperties;
  signatureStyle?: ViewStyle;
  subformStyle?: ViewStyle;
  tagStyle?: ViewStyle;
  tagItemStyle?: ViewStyle;
  tagTextStyle?: CSSProperties;
  textFieldStyle?: ViewStyle;
  textStyle?: CSSProperties;
  textNumberOfLines?: number;
  timeTrackerStyle?: ViewStyle;
  todoStyle?: ViewStyle;
  todoTextStyle?: CSSProperties;
  urlStyle?: ViewStyle;
  urlTextStyle?: CSSProperties;
  inputButtonStyle?: ViewStyle;
  entityBatchStyle?: ViewStyle;
}

export function CardCollectionViewField({
  field,
  component,
  workflowTemplateId,
  entityId,
  fieldStyles,
  showLabel,
  canUpdateCard,
  analytics,
}: Readonly<Props>) {
  if (isInputButtonComponent(component)) {
    const paddingTopWithLabel = grid(2);
    const paddingTop = showLabel ? paddingTopWithLabel : 0;
    return (
      <InputButton
        component={component}
        style={fieldStyles.inputButtonStyle}
        workflowTemplateId={workflowTemplateId}
        entityId={entityId}
        paddingTop={paddingTop}
        canUpdateCard={canUpdateCard}
        analytics={analytics}
      />
    );
  }

  if (isNil(field)) {
    return null;
  }

  switch (field.fieldType) {
    case 'AddressField':
      return <AddressCollectionViewField field={field} style={fieldStyles.addressStyle} />;
    case 'CombinedTagsField':
      return <CombinedTagsCollectionViewField field={field} style={fieldStyles.tagStyle} />;
    case 'DateField':
      return (
        <DateCollectionViewField
          field={field}
          style={fieldStyles.dateStyle}
          textStyle={fieldStyles.dateTextStyle}
        />
      );
    case 'DateRangeField':
      return (
        <DateRangeCollectionViewField
          field={field}
          style={fieldStyles.dateRangeStyle}
          textStyle={fieldStyles.dateRangeTextStyle}
        />
      );
    case 'EmailField':
      return <EmailCollectionViewField field={field} style={fieldStyles.emailStyle} />;
    case 'EntityBatch':
      return (
        <EntityBatchCollectionViewField
          field={field}
          style={fieldStyles.entityBatchStyle}
          textStyle={fieldStyles.textStyle}
        />
      );
    case 'FileField':
      return <FileCollectionViewField field={field} style={fieldStyles.fileStyle} />;
    case 'GeolocationField':
      return <GeolocationCollectionViewField field={field} style={fieldStyles.geolocationStyle} />;
    case 'NumberField':
      return <NumberCollectionViewField field={field} style={fieldStyles.numberStyle} />;
    case 'PersonField':
      return (
        <PersonCollectionViewField
          field={field}
          style={fieldStyles.personStyle}
          personPillStyle={fieldStyles.personPillStyle}
          personPillTextStyle={fieldStyles.personPillTextStyle}
        />
      );
    case 'RelatedEntityField':
      return (
        <RelatedCardCollectionViewField
          field={field}
          component={component}
          style={fieldStyles.relatedCardStyle}
          pillStyle={fieldStyles.relatedCardPillStyle}
          pillTextStyle={fieldStyles.relatedCardPillTextStyle}
        />
      );
    case 'ScheduledAutomationField':
      return (
        <ScheduledAutomationCollectionViewField
          field={field}
          style={fieldStyles.scheduledAutomationStyle}
          textStyle={fieldStyles.scheduledAutomationTextStyle}
        />
      );
    case 'SignatureField':
      return <SignatureCollectionViewField field={field} style={fieldStyles.signatureStyle} />;
    case 'SubformField':
      return <SubformCollectionViewField field={field} style={fieldStyles.subformStyle} />;
    case 'TagField':
      return (
        <TagCollectionViewField
          field={field}
          style={fieldStyles.tagStyle}
          tagItemStyle={fieldStyles.tagItemStyle}
          tagTextStyle={fieldStyles.tagTextStyle}
        />
      );
    case 'TextField': {
      return (
        <TextCollectionViewField
          field={field}
          style={fieldStyles.textFieldStyle}
          textStyle={fieldStyles.textStyle}
          numberOfLines={fieldStyles.textNumberOfLines}
        />
      );
    }
    case 'TimeTrackerField':
      return (
        <>
          {isTimeTrackerComponent(component) && (
            <TimeTrackerCollectionViewField
              component={component}
              entityId={entityId}
              field={field}
              style={fieldStyles.timeTrackerStyle}
              canUpdateCard={canUpdateCard}
            />
          )}
        </>
      );
    case 'TodoField':
      return (
        <TodoCollectionViewField
          field={field}
          style={fieldStyles.todoStyle}
          textStyle={fieldStyles.todoTextStyle}
        />
      );
    case 'UrlField':
      return (
        <UrlCollectionViewField
          field={field}
          style={fieldStyles.urlStyle}
          textStyle={fieldStyles.urlTextStyle}
        />
      );
    default:
      return <UnknownCollectionViewField field={field} />;
  }
}
